import { fetchBaseQuery, retry } from "@reduxjs/toolkit/query";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query";
import { appLoaderActions } from "@lxp-app/store/slices/appLoaderSlice";
import { ClientError, request } from "graphql-request";
import messageCode from "@lxp-app/assets/config/message-code-fe.json";
import { v4 as uuidv4 } from "uuid";
import { KeysOf } from "@lxp-app/store/slices/query-args/searchCatalog";
import Urls from "@lxp-app/utils/urls";

type ExtraOptions = {
  service?: KeysOf<typeof Urls>;
};

interface CustomFetchArgs extends FetchArgs {
  customuuid?: string;
  url: `/${string | "graphql"}`;
}

type CustomBaseQueryFn = BaseQueryFn<
  CustomFetchArgs,
  unknown,
  FetchBaseQueryError,
  ExtraOptions
>;

const baseQuery = retry(
  fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_TMS_URL}`,
    prepareHeaders(headers, { getState }: any) {
      const uid = getState().UserInfoSlice.profile.id;
      const org = getState().UserInfoSlice.org;
      const token = getState().UserInfoSlice.token;
      headers.set("x-muse-locale", "en_IN");
      if (!headers.has("x-muse-uuid") && uid != undefined) {
        headers.set("x-muse-uuid", uid);
      }
      headers.set("x-muse-version", "v1");
      headers.set("x-muse-org", org);
      headers.set("x-muse-zone", "Asia/Kolkata");
      headers.set("Accept", "*/*");
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("x-muse-request-id", `RQ${uuidv4()}`);
      headers.set("x-muse-caller", "glx");
      return headers;
    }
  }),
  { maxRetries: 0 }
);

const customBaseQuery: CustomBaseQueryFn = async (args, api, extraOptions) => {
  const { dispatch } = api;
  const getState: () => any = api.getState;
  dispatch(appLoaderActions.setIsLoading(true));
  const { method, body, customuuid } = args;
  try {
    const REST = Boolean(method);
    const uuid = customuuid ? customuuid : getState().UserInfoSlice.profile.id;
    const org = getState().UserInfoSlice.org;
    const token = getState().UserInfoSlice.token;
    const header = {
      "x-muse-uuid": uuid,
      "x-muse-org": org,
      "x-muse-version": "v1",
      "x-muse-zone": "Asia/Kolkata",
      "x-muse-locale": "en_IN",
      "x-muse-request-id": `RQ${uuidv4()}`,
      "x-muse-caller": "glx",
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`
    };

    const updatedArgs = { ...args };

    if (extraOptions?.service) {
      updatedArgs.url = (Urls[extraOptions.service!] + updatedArgs.url) as any;
    } else {
      if (!REST) {
        updatedArgs.url = (Urls["TMS"] + updatedArgs.url) as any;
      }
    }

    const result = REST
      ? await baseQuery(
          {
            ...updatedArgs,
            headers: {
              ...updatedArgs.headers,
              ...(uuid != undefined && { "x-muse-uuid": uuid })
            }
          },
          api,
          {
            maxRetries: 0
          }
        )
      : ((await request(
          updatedArgs.url,
          body,
          args?.params?.variables,
          header
        )) as any);
    dispatch(appLoaderActions.setIsLoading(false));
    return REST ? result : { data: result };
  } catch (error) {
    dispatch(appLoaderActions.setIsLoading(false));
    if (error instanceof ClientError) {
      return { error: { status: error.response.status, data: error } };
    }
    return { error: { status: 500, data: error } };
  }
};

export default customBaseQuery;

type RemoveErrorSuffix<T extends string> = T extends `${infer Prefix}_error`
  ? Prefix
  : T;
type AddErrorSuffix<T extends string> = `${T}_error`;

export interface IBaseQueryResponse<T> {
  timestamp: string;
  data: T;
  error: RemoveErrorSuffix<AddErrorSuffix<keyof typeof messageCode>>[];
  version: string;
}

export type IBaseMutationResponse = IBaseQueryResponse<{
  id: string;
  message: RemoveErrorSuffix<AddErrorSuffix<keyof typeof messageCode>>;
}>;
